// src/features/project/logic.ts
// Framework-agnostic business logic for Projects.
// Zero React imports — pure TypeScript functions and data maps.

import type { Project } from '@core/types/project';

// ---------------------------------------------------------------------------
// Accent color swatches — projects, components, labels
// ---------------------------------------------------------------------------

export const PROJECT_ACCENT_COLORS: readonly { value: string; label: string }[] = [
  { value: '#6366f1', label: 'Indigo' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#84cc16', label: 'Lime' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#f97316', label: 'Orange' },
  { value: '#ef4444', label: 'Red' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#64748b', label: 'Slate' },
];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatDate(dateStr: string | undefined | null, locale = 'en'): string {
  if (!dateStr) return '--';
  // Go zero time ('0001-01-01…') means the field was never set.
  if (new Date(dateStr).getFullYear() < 1971) return '--';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateStr));
  } catch {
    return String(dateStr);
  }
}

export function formatDateTime(dateStr: string | undefined | null, locale = 'en'): string {
  if (!dateStr) return '--';
  // Go zero time ('0001-01-01…') means the field was never set.
  if (new Date(dateStr).getFullYear() < 1971) return '--';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr));
  } catch {
    return String(dateStr);
  }
}

export function formatRelativeTime(dateStr: string | undefined | null, locale = 'en'): string {
  if (!dateStr) return '--';
  // Go zero time ('0001-01-01…') means the field was never set.
  if (new Date(dateStr).getFullYear() < 1971) return '--';
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const sec = Math.floor(diff / 1000);
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
    if (sec < 60) return rtf.format(-sec, 'second');
    const min = Math.floor(sec / 60);
    if (min < 60) return rtf.format(-min, 'minute');
    const hr = Math.floor(min / 60);
    if (hr < 24) return rtf.format(-hr, 'hour');
    const day = Math.floor(hr / 24);
    if (day < 7) return rtf.format(-day, 'day');
    if (day < 30) return rtf.format(-Math.floor(day / 7), 'week');
    return formatDate(dateStr, locale);
  } catch {
    return String(dateStr);
  }
}

export function formatProgress(completed: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((completed / total) * 100);
}

// ---------------------------------------------------------------------------
// Avatar / color helpers
// ---------------------------------------------------------------------------

const AVATAR_COLORS = [
  'hsl(239 84% 67%)',
  'hsl(330 81% 60%)',
  'hsl(38 92% 50%)',
  'hsl(160 84% 39%)',
  'hsl(217 91% 60%)',
  'hsl(258 90% 66%)',
  'hsl(0 84% 60%)',
  'hsl(174 84% 40%)',
];

export function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// ---------------------------------------------------------------------------
// Entity ID extractor
// ---------------------------------------------------------------------------

export function getEntityId(item: any): string {
  return item?.entity?.id || item?.id || '';
}

export function getDisplayName(actor: any): string {
  return actor?.display_name || actor?.name || '--';
}

export function getProjectName(item: any): string {
  return String(item?.name ?? '');
}

// ---------------------------------------------------------------------------
// Status → Badge variant maps (return i18n keys for labels)
// ---------------------------------------------------------------------------

export type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'primary' | 'secondary';

interface StatusMapping {
  variant: BadgeVariant;
  labelKey: string;
}

export const projectStatusMap: Record<string, StatusMapping> = {
  PROJECT_STATUS_PLANNING: { variant: 'secondary', labelKey: 'project.status.planning' },
  PROJECT_STATUS_ACTIVE: { variant: 'success', labelKey: 'project.status.active' },
  PROJECT_STATUS_ON_HOLD: { variant: 'warning', labelKey: 'project.status.onHold' },
  PROJECT_STATUS_COMPLETED: { variant: 'primary', labelKey: 'project.status.completed' },
  PROJECT_STATUS_CANCELLED: { variant: 'error', labelKey: 'project.status.cancelled' },
  PROJECT_STATUS_ARCHIVED: { variant: 'secondary', labelKey: 'project.status.archived' },
};

export const methodologyMap: Record<string, { labelKey: string; descriptionKey: string }> = {
  PROJECT_METHODOLOGY_KANBAN: { labelKey: 'project.methodology.kanban', descriptionKey: 'project.methodology.kanbanDesc' },
  PROJECT_METHODOLOGY_SCRUM: { labelKey: 'project.methodology.scrum', descriptionKey: 'project.methodology.scrumDesc' },
  PROJECT_METHODOLOGY_WATERFALL: { labelKey: 'project.methodology.waterfall', descriptionKey: 'project.methodology.waterfallDesc' },
  PROJECT_METHODOLOGY_HYBRID: { labelKey: 'project.methodology.hybrid', descriptionKey: 'project.methodology.hybridDesc' },
  PROJECT_METHODOLOGY_CUSTOM: { labelKey: 'project.methodology.custom', descriptionKey: 'project.methodology.customDesc' },
};

export const projectRoleMap: Record<string, StatusMapping> = {
  PROJECT_ROLE_OWNER: { variant: 'primary', labelKey: 'project.role.owner' },
  PROJECT_ROLE_ADMIN: { variant: 'warning', labelKey: 'project.role.admin' },
  PROJECT_ROLE_MANAGER: { variant: 'success', labelKey: 'project.role.manager' },
  PROJECT_ROLE_MEMBER: { variant: 'secondary', labelKey: 'project.role.member' },
  PROJECT_ROLE_VIEWER: { variant: 'default', labelKey: 'project.role.viewer' },
};

export function getStatusMapping(
  map: Record<string, StatusMapping>,
  status: string | undefined,
): StatusMapping {
  if (!status) return { variant: 'secondary', labelKey: 'common.unknown' };
  return map[status] ?? { variant: 'secondary', labelKey: 'common.unknown' };
}

export function getMethodologyInfo(methodology: string | undefined) {
  if (!methodology) return null;
  return methodologyMap[methodology] ?? null;
}

// ---------------------------------------------------------------------------
// Filter types
// ---------------------------------------------------------------------------

export type ProjectFilter = 'all' | 'active' | 'planning' | 'on_hold' | 'completed' | 'archived';

export const projectFilterToStatus: Record<ProjectFilter, string | null> = {
  all: null,
  active: 'PROJECT_STATUS_ACTIVE',
  planning: 'PROJECT_STATUS_PLANNING',
  on_hold: 'PROJECT_STATUS_ON_HOLD',
  completed: 'PROJECT_STATUS_COMPLETED',
  archived: 'PROJECT_STATUS_ARCHIVED',
};

export const projectFilters: { key: ProjectFilter; labelKey: string }[] = [
  { key: 'all', labelKey: 'common.all' },
  { key: 'active', labelKey: 'project.status.active' },
  { key: 'planning', labelKey: 'project.status.planning' },
  { key: 'on_hold', labelKey: 'project.status.onHold' },
  { key: 'completed', labelKey: 'project.status.completed' },
  { key: 'archived', labelKey: 'project.status.archived' },
];

export function filterProjects(
  projects: Project[],
  search: string,
  statusFilter: ProjectFilter,
): Project[] {
  let filtered = projects;

  const mappedStatus = projectFilterToStatus[statusFilter];
  if (mappedStatus) {
    filtered = filtered.filter((p: any) => p.status === mappedStatus);
  }

  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter((p: any) =>
      p.name?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q) ||
      p.key?.toLowerCase().includes(q),
    );
  }

  return filtered;
}

export function sortProjectsByActivity(projects: Project[]): Project[] {
  return [...projects].sort((a: any, b: any) => {
    const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
    const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
    return bTime - aTime;
  });
}

// ---------------------------------------------------------------------------
// Permission checks
// ---------------------------------------------------------------------------

export function canEditProject(item: { status?: string }): boolean {
  return !(
    item.status === 'PROJECT_STATUS_ARCHIVED' ||
    item.status === 'PROJECT_STATUS_CANCELLED' ||
    item.status === 'archived' ||
    item.status === 'deleted'
  );
}

export function canDeleteProject(item: { status?: string }): boolean {
  return item.status !== 'deleted';
}

export function canArchiveProject(item: { status?: string }): boolean {
  return (
    item.status === 'PROJECT_STATUS_ACTIVE' ||
    item.status === 'PROJECT_STATUS_ON_HOLD' ||
    item.status === 'PROJECT_STATUS_COMPLETED'
  );
}

// ---------------------------------------------------------------------------
// Validation (returns i18n key strings)
// ---------------------------------------------------------------------------

export function validateProjectForm(data: {
  name: string;
  key: string;
  description?: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!data.name.trim()) {
    errors.name = 'validation.required';
  } else if (data.name.trim().length < 2) {
    errors.name = 'validation.minLength';
  } else if (data.name.trim().length > 100) {
    errors.name = 'validation.maxLength';
  }

  if (!data.key.trim()) {
    errors.key = 'validation.required';
  } else if (!/^[A-Z][A-Z0-9_]{1,9}$/.test(data.key.trim())) {
    errors.key = 'project.validation.keyFormat';
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Methodology options for select
// ---------------------------------------------------------------------------

export const methodologyOptions = [
  { value: 'PROJECT_METHODOLOGY_KANBAN', labelKey: 'project.methodology.kanban' },
  { value: 'PROJECT_METHODOLOGY_SCRUM', labelKey: 'project.methodology.scrum' },
  { value: 'PROJECT_METHODOLOGY_WATERFALL', labelKey: 'project.methodology.waterfall' },
  { value: 'PROJECT_METHODOLOGY_HYBRID', labelKey: 'project.methodology.hybrid' },
  { value: 'PROJECT_METHODOLOGY_CUSTOM', labelKey: 'project.methodology.custom' },
];

export const statusOptions = [
  { value: 'PROJECT_STATUS_PLANNING', labelKey: 'project.status.planning' },
  { value: 'PROJECT_STATUS_ACTIVE', labelKey: 'project.status.active' },
  { value: 'PROJECT_STATUS_ON_HOLD', labelKey: 'project.status.onHold' },
  { value: 'PROJECT_STATUS_COMPLETED', labelKey: 'project.status.completed' },
];

// ---------------------------------------------------------------------------
// Project workspace tabs
// ---------------------------------------------------------------------------

export type ProjectTab = 'backlog' | 'board' | 'timeline' | 'sprints' | 'members' | 'summary';

export interface ProjectTabConfig {
  id: ProjectTab;
  labelKey: string;
  iconPath: string;
}

export const PROJECT_TABS: ProjectTabConfig[] = [
  {
    id: 'backlog',
    labelKey: 'project.tab.backlog',
    iconPath: 'M4 6h16M4 10h16M4 14h16M4 18h16',
  },
  {
    id: 'board',
    labelKey: 'project.tab.board',
    iconPath: 'M9 4h6v16H9zM3 4h6v10H3zM15 4h6v12h-6z',
  },
  {
    id: 'timeline',
    labelKey: 'project.tab.timeline',
    iconPath: 'M3 12h4l3-9 4 18 3-9h4',
  },
  {
    id: 'sprints',
    labelKey: 'project.tab.sprints',
    iconPath: 'M13 10V3L4 14h7v7l9-11h-7z',
  },
  {
    id: 'members',
    labelKey: 'project.tab.members',
    iconPath: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13.5 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z',
  },
  {
    id: 'summary',
    labelKey: 'project.tab.summary',
    iconPath: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  },
];

export const VALID_PROJECT_TABS = new Set<string>(PROJECT_TABS.map(t => t.id));

export function resolveProjectTab(raw: string | null): ProjectTab {
  if (raw && VALID_PROJECT_TABS.has(raw)) return raw as ProjectTab;
  return 'backlog';
}

// ---------------------------------------------------------------------------
// Project workspace stats (computed from task items)
// ---------------------------------------------------------------------------

export interface ProjectWorkspaceStats {
  totalTasks: number;
  completedTasks: number;
  openTasks: number;
  overdueCount: number;
  memberCount: number;
}

export function computeProjectWorkspaceStats(
  tasks: any[],
  memberCount: number,
): ProjectWorkspaceStats {
  let completedTasks = 0;
  let overdueCount = 0;

  const now = Date.now();

  for (const task of tasks) {
    const status = task?.status || '';
    if (status === 'TASK_STATUS_DONE' || status === 'TASK_STATUS_CLOSED') {
      completedTasks++;
    }

    const dueDate = task?.due_date;
    if (
      dueDate &&
      status !== 'TASK_STATUS_DONE' &&
      status !== 'TASK_STATUS_CLOSED'
    ) {
      try {
        if (new Date(dueDate).getTime() < now) {
          overdueCount++;
        }
      } catch {
        // ignore invalid dates
      }
    }
  }

  return {
    totalTasks: tasks.length,
    completedTasks,
    openTasks: tasks.length - completedTasks,
    overdueCount,
    memberCount,
  };
}

// ---------------------------------------------------------------------------
// Sprint display helpers
// ---------------------------------------------------------------------------

export interface SprintDisplayInfo {
  id: string;
  name: string;
  startDate: string | undefined;
  endDate: string | undefined;
  status: string;
  totalItems: number;
  completedItems: number;
}

export function formatSprintProgress(totalItems: number, completedItems: number): number {
  if (totalItems === 0) return 0;
  return Math.round((completedItems / totalItems) * 100);
}

export const sprintStatusMap: Record<string, StatusMapping> = {
  SPRINT_STATUS_PLANNING: { variant: 'secondary', labelKey: 'project.sprint.status.planning' },
  SPRINT_STATUS_ACTIVE: { variant: 'success', labelKey: 'project.sprint.status.active' },
  SPRINT_STATUS_COMPLETED: { variant: 'primary', labelKey: 'project.sprint.status.completed' },
  SPRINT_STATUS_CANCELLED: { variant: 'error', labelKey: 'project.sprint.status.cancelled' },
};

// ---------------------------------------------------------------------------
// Quick-link navigation items for project detail page
// ---------------------------------------------------------------------------

export interface ProjectQuickLink {
  labelKey: string;
  path: string;
  descriptionKey: string;
  iconPath: string;
  countField?: string;
}

export const projectQuickLinks: ProjectQuickLink[] = [
  {
    labelKey: 'project.nav.boards',
    path: 'boards',
    descriptionKey: 'project.nav.boardsDesc',
    iconPath: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2',
  },
  {
    labelKey: 'project.nav.items',
    path: 'items',
    descriptionKey: 'project.nav.itemsDesc',
    iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
    countField: 'item_count',
  },
  {
    labelKey: 'project.nav.sprints',
    path: 'sprints',
    descriptionKey: 'project.nav.sprintsDesc',
    iconPath: 'M13 10V3L4 14h7v7l9-11h-7z',
  },
  {
    labelKey: 'project.nav.milestones',
    path: 'milestones',
    descriptionKey: 'project.nav.milestonesDesc',
    iconPath: 'M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9',
  },
  {
    labelKey: 'project.nav.epics',
    path: 'epics',
    descriptionKey: 'project.nav.epicsDesc',
    iconPath: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z',
  },
  {
    labelKey: 'project.nav.members',
    path: 'members',
    descriptionKey: 'project.nav.membersDesc',
    iconPath: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z',
    countField: 'member_count',
  },
  {
    labelKey: 'project.nav.labels',
    path: 'labels',
    descriptionKey: 'project.nav.labelsDesc',
    iconPath: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
  },
  {
    labelKey: 'project.nav.analytics',
    path: 'analytics',
    descriptionKey: 'project.nav.analyticsDesc',
    iconPath: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  },
  {
    labelKey: 'project.nav.settings',
    path: 'settings',
    descriptionKey: 'project.nav.settingsDesc',
    iconPath: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  },
];
